import type { TokenResponse } from '../types'
import { Sentry } from '../lib/sentry'

// API base URL - configurable via environment variable
// In development: uses Vite proxy (/api -> localhost:8080)
// In production: set VITE_API_URL to the API server URL
export const API_BASE = import.meta.env.VITE_API_URL || '/api'

// Token storage keys
const ACCESS_TOKEN_KEY = 'factory_access_token'
const REFRESH_TOKEN_KEY = 'factory_refresh_token'

// Token management
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

// Check if user is authenticated
export function isAuthenticated(): boolean {
  return !!getAccessToken()
}

/** Error thrown by fetchAPI for non-OK responses: carries the HTTP status and the decoded body. */
export type FetchError = Error & { status?: number; data?: Record<string, unknown> }

/** Window event fired when the session can no longer be refreshed. */
export const AUTH_EXPIRED_EVENT = 'factory:auth-expired'

export async function fetchAPI<T>(endpoint: string, options?: RequestInit & { skipAuth?: boolean }): Promise<T> {
  const headers: Record<string, string> = {
    ...(options?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...options?.headers as Record<string, string>,
  }

  // Add auth header if token exists and not skipped
  if (!options?.skipAuth) {
    const token = getAccessToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  // Preserve body for potential 401 retry (body streams can only be consumed once)
  const body = options?.body

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
    body,
  })

  // Handle 401 - try to refresh token
  if (response.status === 401 && !options?.skipAuth) {
    const refreshed = await tryRefreshToken()
    if (refreshed) {
      // Retry the request with new token
      headers['Authorization'] = `Bearer ${getAccessToken()}`
      const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
        body, // explicitly pass preserved body for retry
      })
      if (!retryResponse.ok) {
        const error = await retryResponse.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(error.error || `HTTP ${retryResponse.status}`)
      }
      return retryResponse.json()
    } else {
      // Refresh failed: clear tokens and tell the app (AuthContext signs the
      // user out, so protected routes redirect to login instead of every page
      // showing "Session expired" errors while still looking signed in).
      clearTokens()
      if (typeof window !== 'undefined') window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
      throw new Error('Session expired. Please login again.')
    }
  }

  // Handle 403 email_not_verified — the JWT may be stale if verification
  // happened in a different browser/tab.  Refresh the token (which reads
  // email_verified from the database) and retry once.
  if (response.status === 403 && !options?.skipAuth) {
    const data403 = await response.clone().json().catch(() => ({}))
    if (data403.error === 'email_not_verified') {
      const refreshed = await tryRefreshToken()
      if (refreshed) {
        headers['Authorization'] = `Bearer ${getAccessToken()}`
        const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
          ...options,
          headers,
          body,
        })
        if (retryResponse.ok) {
          return retryResponse.json()
        }
        // Still 403 after refresh — email genuinely not verified
        const retryError = await retryResponse.json().catch(() => ({ error: 'email_not_verified' }))
        throw new Error(retryError.error || 'Email not verified')
      }
    }
  }

  // Handle 402 - billing limit exceeded
  if (response.status === 402) {
    const data = await response.json().catch(() => ({ message: 'Usage limit exceeded' }))
    const err = new Error(data.message || 'Usage limit exceeded') as Error & { code?: string; upgradeUrl?: string; limitType?: string }
    err.code = data.code
    err.upgradeUrl = data.upgrade_url
    err.limitType = data.limit_type
    throw err
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    const err = new Error(error.error || `HTTP ${response.status}`) as FetchError
    err.status = response.status
    err.data = error
    // Capture server errors (5xx) in Sentry
    if (response.status >= 500) {
      Sentry.captureException(err, {
        tags: { endpoint, status: response.status },
      })
    }
    throw err
  }

  return response.json()
}

// Mutex for token refresh — prevents concurrent 401 retries from racing
let refreshPromise: Promise<boolean> | null = null

// Try to refresh the access token (serialized — only one refresh at a time)
async function tryRefreshToken(): Promise<boolean> {
  // If a refresh is already in-flight, wait for it instead of starting another
  if (refreshPromise) return refreshPromise

  refreshPromise = doRefreshToken()
  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

async function doRefreshToken(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) {
      console.warn('[fetchAPI] Token refresh failed:', response.status)
      return false
    }

    const tokens: TokenResponse = await response.json()
    setTokens(tokens.access_token, tokens.refresh_token)
    return true
  } catch (err) {
    console.warn('[fetchAPI] Token refresh error:', err)
    return false
  }
}

// Capabilities (public endpoint — plan/pricing data, no auth required)
export interface PlanLimits {
  max_projects: number
  max_agents: number
  max_workflow_executions: number
  max_test_runs: number
  max_code_reviews: number
  max_api_calls_daily: number
  max_agent_minutes_monthly: number
  max_chat_messages_daily: number
  max_storage_bytes: number
}

export interface PlanInfo {
  id: string
  name: string
  price_monthly_cents: number
  per_seat: boolean
  limits: PlanLimits
  features?: string[]
}

export interface CapabilitiesResponse {
  plans?: PlanInfo[]
}

export async function getCapabilities(): Promise<CapabilitiesResponse> {
  return fetchAPI('/capabilities', { skipAuth: true })
}

// Health & Version
export async function getHealth(): Promise<{ status: string; version: string }> {
  return fetchAPI('/health')
}

export async function getVersion(): Promise<{ version: string }> {
  return fetchAPI('/version')
}
